// src/features/feed/twoHref.ts
/**
 * .tuMetrics {"tu1": 0.94, "tu2": 0.95, "tu3": 2, "notes": "Pure /2?lens= hrefs from feed events; no stores", "tuVersion": "2.5"}
 * .help "Turns a mention or DM into the 2.0 address you can put on a notification or push payload."
 */
import { twoHrefFromLens } from '../stream/lens.ts';

export function feedStreamRecipientId(event: {
  type?: string | null;
  streamType?: string | null;
  recipientStreamId?: string | null;
  initiatorId?: string | null;
}): string {
  const isDm =
    event.streamType === 'direct_message' || event.type === 'direct_message_received';
  if (isDm) {
    // Notifications are addressed to me; recipient_stream_id is often also me.
    // The other person is the initiator (sender).
    return event.initiatorId || event.recipientStreamId || '';
  }
  return event.recipientStreamId || '';
}

/** @deprecated 1.0 chat routes — kept for old tests; product navigates via twoHref / one mic. */
export function oneHrefFromFeedEvent(event: {
  type?: string | null;
  streamType?: string | null;
  recipientStreamId?: string | null;
  initiatorId?: string | null;
}): string | null {
  const streamType =
    event.streamType ||
    (event.type === 'direct_message_received' ? 'direct_message' : null);
  const recipientId = feedStreamRecipientId({ ...event, streamType });
  if (streamType === 'direct_message' && recipientId) {
    return `/streams/dm/${recipientId}`;
  }
  if (streamType === 'team_stream' && recipientId) {
    return `/streams/team/${recipientId}`;
  }
  if (streamType === 'standup_stream' && recipientId) {
    return `/streams/standup/${recipientId}`;
  }
  if (streamType === 'company_stream') {
    return '/streams/company';
  }
  return null;
}

export function twoHrefFromFeedEvent(event: {
  type?: string | null;
  streamType?: string | null;
  recipientStreamId?: string | null;
  initiatorId?: string | null;
  projectId?: string | null;
  sprintId?: string | null;
  roadmapId?: string | null;
  metadata?: { guide_id?: string } | null;
}): string {
  if (event.type === 'guide_published' || event.type === 'guide_draft_updated' || event.type === 'guide_author_added') {
    const guideId = event.metadata?.guide_id;
    return twoHrefFromLens({ kind: 'guides' }) + (guideId ? `&guide=${encodeURIComponent(guideId)}` : '');
  }
  if (event.type === 'plan_draft_updated' || event.type === 'plan_author_added') {
    const planId =
      (event.metadata as { plan_id?: string } | null | undefined)?.plan_id ||
      (event as { planId?: string | null }).planId;
    return planId
      ? twoHrefFromLens({ kind: 'plan', id: planId })
      : twoHrefFromLens({ kind: 'inventory', id: 'plans' });
  }
  if (event.streamType && event.recipientStreamId) {
    const recipientId = feedStreamRecipientId(event);
    if (event.streamType === 'direct_message' && recipientId) {
      return twoHrefFromLens({ kind: 'room', streamType: 'dm', id: recipientId });
    }
    if (event.streamType === 'team_stream' && recipientId) {
      return twoHrefFromLens({ kind: 'room', streamType: 'team', id: recipientId });
    }
    if (event.streamType === 'standup_stream' && recipientId) {
      return twoHrefFromLens({ kind: 'room', streamType: 'standup', id: recipientId });
    }
    if (event.streamType === 'company_stream') {
      return twoHrefFromLens({ kind: 'room', streamType: 'company', id: '' });
    }
  }
  if (event.projectId) return twoHrefFromLens({ kind: 'project', id: event.projectId });
  if (event.sprintId) return twoHrefFromLens({ kind: 'sprint', id: event.sprintId });
  if (event.roadmapId) return twoHrefFromLens({ kind: 'roadmap', id: event.roadmapId });
  return '/';
}
